import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LegacyJob, LegacyCity } from '../types';
import { isRemoteWorkStyle } from '../src/utils/workStyleUtils';

// 使用 Legacy 类型用于向后兼容
type Job = LegacyJob;
type City = LegacyCity;

interface StructuredDataProps {
  type?: 'organization' | 'jobPosting' | 'breadcrumb';
  job?: Job;
  city?: City;
  breadcrumbItems?: { name: string; url: string }[];
}

/**
 * StructuredData Component
 *
 * Injects JSON-LD structured data into the document head for SEO.
 * Supports Google's Job Posting and Organization schemas.
 *
 * @see https://developers.google.com/search/docs/appearance/structured-data/job-posting
 */
const StructuredData: React.FC<StructuredDataProps> = ({
  type = 'organization',
  job,
  city,
  breadcrumbItems
}) => {
  const { i18n } = useTranslation();
  const baseUrl = 'https://careers.uhomes.com';
  const currentLang = i18n.language;

  useEffect(() => {
    // Remove existing structured data script
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    let structuredData: Record<string, unknown> = {};

    switch (type) {
      case 'organization':
        structuredData = getOrganizationSchema();
        break;
      case 'jobPosting':
        structuredData = job ? getJobPostingSchema(job) : {};
        break;
      case 'breadcrumb':
        structuredData = breadcrumbItems ? getBreadcrumbSchema(breadcrumbItems) : {};
        break;
    }

    // Create and inject script tag
    if (Object.keys(structuredData).length > 0) {
      const script = document.createElement('script');
      script.id = 'structured-data';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      const script = document.getElementById('structured-data');
      if (script) {
        script.remove();
      }
    };
  }, [type, job, city, breadcrumbItems, currentLang]);

  // Organization Schema - Company information
  const getOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'UHomes',
    alternateName: 'uhomes.com',
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    description: currentLang === 'zh'
      ? 'UHomes是全球领先的留学生住宿服务平台，致力于为全球学生提供可靠、安全、便捷的住宿解决方案。业务覆盖全球33个城市，服务超过300万留学生。'
      : 'UHomes is a leading global student accommodation platform, dedicated to providing reliable, safe, and convenient housing solutions for students worldwide. Covering 33 cities globally, serving over 3 million students.',
    sameAs: [
      'https://www.uhomes.com',
      'https://www.linkedin.com/company/uhomes',
      'https://www.facebook.com/uhomes.com',
      'https://www.instagram.com/uhomes.global'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'careers',
      url: `${baseUrl}/jobs`
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: 'Beijing',
      streetAddress: 'Beijing, China'
    },
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: '1200+'
    },
    areaServed: CITIES.map(c => ({
      '@type': 'City',
      name: c.enName,
      alternateName: c.name
    }))
  });

  // Job Posting Schema - Individual job information
  const getJobPostingSchema = (jobData: Job) => {
    const jobUrl = `${baseUrl}/jobs/${jobData.id}`;
    const cityData = CITIES.find(c => c.id === jobData.city);

    // Format posted date to ISO string
    const postedDate = jobData.postedDate instanceof Date
      ? jobData.postedDate.toISOString()
      : new Date(jobData.postedDate).toISOString();

    // Calculate valid through date (90 days from posted date)
    const validThrough = new Date(postedDate);
    validThrough.setDate(validThrough.getDate() + 90);

    // Get employment type
    const employmentTypeMap: Record<string, string> = {
      'FULL_TIME': 'FULL_TIME',
      'PART_TIME': 'PART_TIME',
      'INTERNSHIP_ONLINE': 'INTERN',
      'INTERNSHIP_OFFLINE': 'INTERN',
      'REMOTE': 'FULL_TIME',
      'REMOTE_GLOBAL': 'FULL_TIME'
    };

    // Format benefits array
    const benefitsList = jobData.benefits?.map(b =>
      typeof b === 'string' ? b : (currentLang === 'zh' ? b.zh : b.en)
    ) || [];

    return {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: jobData.title,
      description: formatJobDescription(jobData),
      identifier: {
        '@type': 'PropertyValue',
        name: 'UHomes',
        value: jobData.id
      },
      datePosted: postedDate.split('T')[0],
      validThrough: validThrough.toISOString().split('T')[0],
      employmentType: employmentTypeMap[jobData.type] || 'FULL_TIME',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'UHomes',
        alternateName: 'uhomes.com',
        url: baseUrl,
        logo: `${baseUrl}/logo.png`
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressCountry: getCountryCode(jobData.country),
          addressLocality: jobData.location,
          streetAddress: jobData.location
        }
      },
      applicantLocationRequirements: isRemoteWorkStyle(jobData.workStyle) || jobData.type.includes('REMOTE')
        ? {
            '@type': 'Country',
            name: 'Worldwide'
          }
        : undefined,
      jobLocationType: isRemoteWorkStyle(jobData.workStyle) || jobData.type.includes('REMOTE')
        ? 'TELECOMMUTE'
        : undefined,
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 1,
          unitText: jobData.type.includes('INTERNSHIP') ? 'MONTH' : 'YEAR'
        }
      },
      experienceRequirements: {
        '@type': 'OccupationalExperienceRequirements',
        monthsOfExperience: jobData.type.includes('INTERNSHIP') ? 0 : 36
      },
      skills: extractSkills(jobData.requirements || []),
      responsibilities: jobData.description || [],
      benefits: benefitsList,
      url: jobUrl
    };
  };

  // Breadcrumb Schema - Navigation breadcrumbs
  const getBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  });

  // Helper: Format job description for schema
  const formatJobDescription = (jobData: Job): string => {
    const sections: string[] = [];

    if (jobData.description && jobData.description.length > 0) {
      sections.push('**About the Role**\n' + jobData.description.join('\n'));
    }

    if (jobData.requirements && jobData.requirements.length > 0) {
      sections.push('\n**Requirements**\n' + jobData.requirements.join('\n'));
    }

    if (jobData.benefits && jobData.benefits.length > 0) {
      const benefits = jobData.benefits.map(b =>
        typeof b === 'string' ? b : (currentLang === 'zh' ? b.zh : b.en)
      );
      sections.push('\n**Benefits**\n' + benefits.join('\n'));
    }

    return sections.join('\n\n');
  };

  // Helper: Extract skills from requirements
  const extractSkills = (requirements: string[]): string[] => {
    const skills: string[] = [];
    const skillKeywords = [
      'JavaScript', 'TypeScript', 'React', 'Vue', 'Angular', 'Node.js',
      'Python', 'Java', 'Go', 'SQL', 'NoSQL', 'MongoDB', 'PostgreSQL',
      'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes',
      'Git', 'CI/CD', 'Agile', 'Scrum',
      'Communication', 'Leadership', 'Teamwork',
      'Marketing', 'Sales', 'Business Development',
      'Data Analysis', 'Product Management', 'Design'
    ];

    requirements.forEach(req => {
      skillKeywords.forEach(skill => {
        if (req.toLowerCase().includes(skill.toLowerCase()) && !skills.includes(skill)) {
          skills.push(skill);
        }
      });
    });

    return skills;
  };

  // Helper: Get ISO country code
  const getCountryCode = (country: string): string => {
    const countryMap: Record<string, string> = {
      'China': 'CN',
      'UK': 'GB',
      'USA': 'US',
      'India': 'IN',
      'Vietnam': 'VN',
      'Malaysia': 'MY',
      'Singapore': 'SG',
      'Thailand': 'TH',
      'Indonesia': 'ID',
      'South Korea': 'KR'
    };
    return countryMap[country] || country;
  };

  // This component doesn't render anything visible
  return null;
};

export default StructuredData;

// Import CITIES for organization schema
const CITIES: City[] = [
  { id: 'tj', name: '天津', enName: 'Tianjin', country: 'China', jobCount: 10, image: '' },
  { id: 'bj', name: '北京', enName: 'Beijing', country: 'China', jobCount: 9, image: '' },
  { id: 'cd', name: '成都', enName: 'Chengdu', country: 'China', jobCount: 0, image: '' },
  { id: 'cq', name: '重庆', enName: 'Chongqing', country: 'China', jobCount: 0, image: '' },
  { id: 'zz', name: '郑州', enName: 'Zhengzhou', country: 'China', jobCount: 0, image: '' },
  { id: 'wh', name: '武汉', enName: 'Wuhan', country: 'China', jobCount: 0, image: '' },
  { id: 'xa', name: '西安', enName: "Xi'an", country: 'China', jobCount: 0, image: '' },
  { id: 'sh', name: '上海', enName: 'Shanghai', country: 'China', jobCount: 0, image: '' },
  { id: 'gz', name: '广州', enName: 'Guangzhou', country: 'China', jobCount: 0, image: '' },
  { id: 'sz', name: '深圳', enName: 'Shenzhen', country: 'China', jobCount: 1, image: '' },
  { id: 'jn', name: '济南', enName: 'Jinan', country: 'China', jobCount: 0, image: '' },
  { id: 'qd', name: '青岛', enName: 'Qingdao', country: 'China', jobCount: 0, image: '' },
  { id: 'nj', name: '南京', enName: 'Nanjing', country: 'China', jobCount: 0, image: '' },
  { id: 'su', name: '苏州', enName: 'Suzhou', country: 'China', jobCount: 0, image: '' },
  { id: 'hz', name: '杭州', enName: 'Hangzhou', country: 'China', jobCount: 0, image: '' },
  { id: 'xm', name: '厦门', enName: 'Xiamen', country: 'China', jobCount: 0, image: '' },
  { id: 'hf', name: '合肥', enName: 'Hefei', country: 'China', jobCount: 0, image: '' },
  { id: 'cs', name: '长沙', enName: 'Changsha', country: 'China', jobCount: 0, image: '' },
  { id: 'dl', name: '大连', enName: 'Dalian', country: 'China', jobCount: 0, image: '' },
  { id: 'hk', name: '香港', enName: 'Hong Kong', country: 'China', jobCount: 2, image: '' },
  { id: 'tp', name: '台北', enName: 'Taipei', country: 'China', jobCount: 0, image: '' },
  { id: 'ldn', name: '伦敦', enName: 'London', country: 'UK', jobCount: 2, image: '' },
  { id: 'mnc', name: '曼彻斯特', enName: 'Manchester', country: 'UK', jobCount: 1, image: '' },
  { id: 'nyc', name: '纽约', enName: 'New York', country: 'USA', jobCount: 1, image: '' },
  { id: 'bst', name: '波士顿', enName: 'Boston', country: 'USA', jobCount: 0, image: '' },
  { id: 'lax', name: '洛杉矶', enName: 'Los Angeles', country: 'USA', jobCount: 0, image: '' },
  { id: 'mum', name: '孟买', enName: 'Mumbai', country: 'India', jobCount: 8, image: '' },
  { id: 'pun', name: '浦那', enName: 'Pune', country: 'India', jobCount: 0, image: '' },
  { id: 'han', name: '河内', enName: 'Hanoi', country: 'Vietnam', jobCount: 4, image: '' },
  { id: 'kul', name: '吉隆坡', enName: 'Kuala Lumpur', country: 'Malaysia', jobCount: 6, image: '' },
  { id: 'sg', name: '新加坡', enName: 'Singapore', country: 'Singapore', jobCount: 1, image: '' },
  { id: 'bgk', name: '曼谷', enName: 'Bangkok', country: 'Thailand', jobCount: 1, image: '' },
  { id: 'jk', name: '雅加达', enName: 'Jakarta', country: 'Indonesia', jobCount: 1, image: '' },
  { id: 'sel', name: '首尔', enName: 'Seoul', country: 'South Korea', jobCount: 3, image: '' }
];
